import React, { useMemo } from 'react'
import { compact, join } from 'lodash'
import { useTranslation } from 'react-i18next'
import { getRoleFromPath, timeDuration, viewMeetingDetail } from 'utils/helper'
import { IUSER_NOTIFICATION_TYPES, ROLES } from 'components/NotificationPanel/types'
import Button from 'components/Common/Button'
import {
  MessageTextWrapper,
  NotificationTextWrapper,
  PlayCommentWrapper,
  PlayCommentHeader,
  PlayCommentTitle,
  PlayCommentSummary,
  PlayCommentFooter,
} from 'components/NotificationPanel/style'
import { sanitize } from 'dompurify'
import { useHistory } from 'react-router-dom'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import NotificationMessageFactory from './notification-messages'
import { getTenantId } from 'utils/getTenantId'
import { AppButton } from 'components/Common/AppButton/AppButton.component'

// Format mentions as React elements for rich display
const formatMessageWithMentionsAsReact = (text: string) => {
  if (!text || typeof text !== 'string') return null

  // Regular expression to find mentions in the format <mention id="__id__">__display__</mention>
  const mentionRegex = /<mention id="([^"]*)">([^<]*)<\/mention>/g

  // If no mentions, just return the text as is
  if (!text.includes('<mention')) {
    return <span>{text}</span>
  }

  // Split the text at mentions and create React elements
  const parts: (string | JSX.Element)[] = []
  let lastIndex = 0

  // Find all mentions and replace them
  text.replace(mentionRegex, (match, id, displayName, index) => {
    // Add text before mention
    if (index > lastIndex) {
      parts.push(text.substring(lastIndex, index))
    }

    // Add styled mention - using more vivid colors and styling
    parts.push(
      <span
        key={`mention-${index}`}
        className="mention-highlight"
        style={{
          color: '#2563eb',
          fontWeight: 600,
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          padding: '2px 4px',
          borderRadius: '4px',
          display: 'inline-block',
          margin: '0 2px',
        }}
      >
        @{displayName}
      </span>
    )

    lastIndex = index + match.length
    return match
  })

  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }

  return <>{parts}</>
}

const Message = ({ notification, handleClose }) => {
  const { t } = useTranslation()
  const userName = useMemo(() => {
    return join(compact([notification?.firstName, notification?.lastName]), ' ')
  }, [notification])
  const user = useSelector((state: RootState) => state.user.user)
  const isAdvisor = user?.roles.includes(ROLES.BSO_ADVISOR) || user?.roles.includes(ROLES.BSO_ADMIN)
  // Extract initials for avatar
  const tenantId = getTenantId()
  const userInitials = useMemo(() => {
    const firstName = notification?.firstName || ''
    const lastName = notification?.lastName || ''
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }, [notification])
  const history = useHistory()
  const userWithBusiness = notification?.businessName
    ? t('notifications.userOfBusiness', { user: userName, business: notification.businessName })
    : userName

  // common notification content
  const renderNotificationContent = () => {
    if ([IUSER_NOTIFICATION_TYPES.CALENDAR_NOTE_TAGGED].includes(notification.notificationType)) {
      return (
        <PlayCommentWrapper>
          <PlayCommentHeader>
            <span className="font-medium text-app-primary-75">
              {t('notifications.headers.notes')}
            </span>
            <span className="text-app-grey-60 text-xs">{timeDuration(notification.createdAt)}</span>
          </PlayCommentHeader>
          <div className="mt-2 break-words">
            {NotificationMessageFactory.getMessage(
              notification.notificationType,
              userName,
              notification.businessName,
              t
            )}
          </div>
          <PlayCommentTitle className="mt-2 break-words">
            {notification.meta.taskName || t('notifications.playName')}
          </PlayCommentTitle>
          {notification.meta.goalSummary && (
            <PlayCommentSummary className="mt-1.5 break-words">
              {notification.meta.goalSummary}
            </PlayCommentSummary>
          )}
          <PlayCommentFooter className="mt-3">
            <Button
              variant="text"
              label={t('notifications.actions.viewComment')}
              className="text-app-primary-75 px-4 py-1.5 rounded-full text-sm hover:bg-app-primary-10 transition-colors duration-200"
              onClick={() => {
                history.push(
                  `/${getRoleFromPath()}/${tenantId}/clients/${
                    notification.meta.clientId
                  }/meetingRecordV2?filter=past&meetingId=${notification.meta.calendarId}`
                )

                handleClose()
              }}
            />
          </PlayCommentFooter>
        </PlayCommentWrapper>
      )
    }

    if (
      [
        IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_ADDED,
        IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_REPLY,
        IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_MENTION,
        IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_REPLY_MENTION,
        IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_THREAD,
        IUSER_NOTIFICATION_TYPES.MEETING_ACTION_ITEM_ADD,
      ].includes(notification.notificationType)
    ) {
      return (
        <PlayCommentWrapper>
          <PlayCommentHeader>
            <span className="font-medium text-app-primary-75">
              {t('notifications.headers.plays')}
            </span>
            <span className="text-app-grey-60 text-xs">{timeDuration(notification.createdAt)}</span>
          </PlayCommentHeader>
          <div className="mt-2 break-words">
            {NotificationMessageFactory.getMessage(
              notification.notificationType,
              userName,
              notification.businessName,
              t
            )}
          </div>
          <PlayCommentTitle className="mt-2 break-words">
            {notification.meta.taskName || t('notifications.playName')}
          </PlayCommentTitle>
          {notification.meta.goalSummary && (
            <PlayCommentSummary className="mt-1.5 break-words">
              {notification.meta.goalSummary}
            </PlayCommentSummary>
          )}
          <PlayCommentFooter className="mt-3">
            <Button
              variant="text"
              label={t('notifications.actions.viewComment')}
              className="text-app-primary-75 px-4 py-1.5 rounded-full text-sm hover:bg-app-primary-10 transition-colors duration-200"
              onClick={() => {
                if (notification.meta.taskId) {
                  if (isAdvisor) {
                    history.push(
                      `/${getRoleFromPath()}/${tenantId}/clients/${notification.meta.businessId}/${
                        notification.meta.goalId || 'other-tasks'
                      }/task?selectedTaskId=${notification.meta.taskId}`
                    )
                  } else {
                    history.push(
                      `/${getRoleFromPath()}/${tenantId}/${
                        notification.meta.goalId || 'other-tasks'
                      }/task?selectedTaskId=${notification.meta.taskId}`
                    )
                  }
                  handleClose()
                }
              }}
            />
          </PlayCommentFooter>
        </PlayCommentWrapper>
      )
    }

    if (
      [
        IUSER_NOTIFICATION_TYPES.SUGGESTED_GOAL_MESSAGE,
        IUSER_NOTIFICATION_TYPES.ACCEPTED_GOAL_MESSAGE,
        IUSER_NOTIFICATION_TYPES.DECLINED_GOAL_MESSAGE,
      ].includes(notification.notificationType)
    ) {
      return (
        <NotificationContainer
          headerText={t('notifications.headers.goalCenter')}
          contentTitle={`${notification.meta.text.split('\n')[0]}`}
          contentValue={`${notification.meta.text.split('\n')[1]}`}
          onAccept={() => {
            const clientId =
              notification.meta?.data?.clientId || notification.meta?.data?.taskUserId
            if (isAdvisor && clientId) {
              history.push(`/${getRoleFromPath()}/${tenantId}/clients/${clientId}/goals`)
            } else {
              history.push(`/${getRoleFromPath()}/${tenantId}/goals`)
            }
            handleClose()
          }}
          onAcceptButtonLabel={t('notifications.actions.checkItOut')}
          timestamp={`${timeDuration(notification.createdAt)}`}
        />
      )
    }

    if (
      [
        IUSER_NOTIFICATION_TYPES.SUGGESTED_PLAY_MESSAGE,
        IUSER_NOTIFICATION_TYPES.ACCEPTED_PLAY_MESSAGE,
        IUSER_NOTIFICATION_TYPES.DECLINED_PLAY_MESSAGE,
      ].includes(notification.notificationType)
    ) {
      return (
        <NotificationContainer
          headerText={t('notifications.headers.play')}
          contentTitle={`${notification.meta.text.split('\n')[0]}`}
          contentValue={`${notification.meta.text.split('\n')[1]}`}
          contentSubValue={`${notification.meta.text.split('\n')[2]}`}
          onAccept={() => {
            const taskId = notification.meta?.data?.taskId
            const goalId = notification.meta?.data?.goalId || 'other-tasks'
            const clientId = notification.meta?.data?.taskUserId
            if (taskId) {
              if (isAdvisor && clientId) {
                history.push(
                  `/${getRoleFromPath()}/${tenantId}/clients/${clientId}/${goalId}/task?selectedTaskId=${taskId}`
                )
              } else {
                history.push(
                  `/${getRoleFromPath()}/${tenantId}/${goalId}/task?selectedTaskId=${taskId}`
                )
              }
            }
            handleClose()
          }}
          onAcceptButtonLabel={t('notifications.actions.checkItOut')}
          timestamp={`${timeDuration(notification.createdAt)}`}
        />
      )
    }

    if (notification.notificationType === IUSER_NOTIFICATION_TYPES.PLAN_READY) {
      const goal = notification.meta?.data?.goalName
      // The business's name comes with the notification only when it is known: else the goal alone
      const business = notification.businessName
      const title = goal
        ? isAdvisor
          ? business
            ? t('notifications.planReady.titleAdvisor', { goal, business })
            : t('notifications.planReady.titleGoal', { goal })
          : t('notifications.planReady.title', { goal })
        : notification.meta?.text ?? ''
      return (
        <NotificationContainer
          headerText={t('notifications.headers.plan')}
          contentTitle={title}
          onAccept={() => {
            // The click goes on to the notification's row, which opens the goal page (its url)
            if (!notification.url) handleClose()
          }}
          onAcceptButtonLabel={t('notifications.actions.reviewPlan')}
          timestamp={`${timeDuration(notification.createdAt)}`}
        />
      )
    }

    const calendarKey =
      notification.notificationType === IUSER_NOTIFICATION_TYPES.CALENDAR_MEETING_RESCHEDULED
        ? 'notifications.calendar.rescheduled'
        : notification.notificationType === IUSER_NOTIFICATION_TYPES.CALENDAR_MEETING_CANCELLED
        ? 'notifications.calendar.cancelled'
        : 'notifications.calendar.scheduled'

    return (
      <div className="py-1">
        {[
          IUSER_NOTIFICATION_TYPES.CALENDAR_MEETING,
          IUSER_NOTIFICATION_TYPES.CALENDAR_MEETING_RESCHEDULED,
          IUSER_NOTIFICATION_TYPES.CALENDAR_MEETING_CANCELLED,
        ].includes(notification.notificationType) && (
          <NotificationContainer
            headerText={t('notifications.headers.calendar')}
            contentTitle={t(calendarKey, { user: userWithBusiness })}
            onAccept={() => {
              const resourceId = notification.meta?.resourceId
              history.push(
                resourceId
                  ? `/${getRoleFromPath()}/${tenantId}/calendar?details=${resourceId}`
                  : `/${getRoleFromPath()}/${tenantId}/calendar`
              )
              handleClose()
            }}
            onAcceptButtonLabel={t('notifications.actions.checkItOut')}
            timestamp={`${timeDuration(notification.createdAt)}`}
          />
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.USER_DISABLED && (
          <>{t('notifications.profile.userDisabled', { user: userWithBusiness })}</>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CHAT && (
          <NotificationContainer
            headerText={t('notifications.headers.messages')}
            contentTitle={t('notifications.chat.sent', { user: userWithBusiness })}
            contentValueReact={
              notification.meta?.messageText || notification.meta?.text
                ? formatMessageWithMentionsAsReact(
                    notification.meta?.messageText || notification.meta?.text || ''
                  )
                : undefined
            }
            onAccept={() => {
              const senderId = notification.senderId
              history.push(
                senderId
                  ? `/${getRoleFromPath()}/${tenantId}/chat/${senderId}`
                  : `/${getRoleFromPath()}/${tenantId}/chat`
              )
              handleClose()
            }}
            onAcceptButtonLabel={t('notifications.actions.checkItOut')}
            timestamp={`${timeDuration(notification.createdAt)}`}
          />
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CHAT_TAG && (
          <NotificationContainer
            headerText={t('notifications.headers.messages')}
            contentTitle={t('notifications.chat.tagged', { user: userName })}
            contentValueReact={formatMessageWithMentionsAsReact(
              notification.meta?.messageText || notification.meta?.text || ''
            )}
            onAccept={() => {
              const tenantId = getTenantId()
              const senderId = notification.senderId
              history.push(
                senderId
                  ? `/${getRoleFromPath()}/${tenantId}/chat/${senderId}`
                  : `/${getRoleFromPath()}/${tenantId}/chat`
              )
              handleClose()
            }}
            onAcceptButtonLabel={t('notifications.actions.checkItOut')}
            timestamp={`${timeDuration(notification.createdAt)}`}
          />
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CHAT_MESSAGE_EDITED && (
          <NotificationContainer
            headerText={t('notifications.headers.messages')}
            contentTitle={t('notifications.chat.edited', { user: userName })}
            contentValueReact={formatMessageWithMentionsAsReact(
              notification.meta?.messageText || notification.meta?.text || ''
            )}
            onAccept={() => {
              const tenantId = getTenantId()
              const senderId = notification.senderId
              history.push(
                senderId
                  ? `/${getRoleFromPath()}/${tenantId}/chat/${senderId}`
                  : `/${getRoleFromPath()}/${tenantId}/chat`
              )
              handleClose()
            }}
            onAcceptButtonLabel={t('notifications.actions.viewChat')}
            timestamp={`${timeDuration(notification.createdAt)}`}
          />
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CLIENT_USER_PROFILE_UPDATE && (
          <>
            {notification?.meta?.notificationGeneratedBy === ROLES.BUSINESS_OWNER
              ? t('notifications.profile.userProfileUpdatedSelf', { user: userWithBusiness })
              : t('notifications.profile.userProfileUpdatedOther', { user: userWithBusiness })}
          </>
        )}
        {notification.notificationType ===
          IUSER_NOTIFICATION_TYPES.CLIENT_BUSINESS_PROFILE_UPDATE && (
          <>
            {notification?.meta?.notificationGeneratedBy === ROLES.BUSINESS_OWNER
              ? t('notifications.profile.businessProfileUpdatedSelf', { user: userWithBusiness })
              : t('notifications.profile.businessProfileUpdatedOther', { user: userWithBusiness })}
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.NEW_CLIENT_ADDED && (
          <>{t('notifications.profile.newClient', { user: userName })}</>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CLIENT_REMOVED && (
          <>{t('notifications.profile.clientRemoved', { user: userName })}</>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.MONTHLY_ATTESTATION_UPDATE && (
          <>
            <MessageTextWrapper> {userName} </MessageTextWrapper> {notification.meta.text}
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.ANNOUNCEMENT && (
          <>
            <span>{t('notifications.profile.announcementPrefix', { user: userName })} </span>
            <MessageTextWrapper>
              <NotificationTextWrapper>
                <div dangerouslySetInnerHTML={{ __html: sanitize(notification.meta.text) }} />
              </NotificationTextWrapper>
              {notification.meta.fileUrl && (
                <a
                  href={notification.meta.fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-app-primary-75 hover:underline mt-2 inline-block"
                >
                  {t('notifications.actions.viewAttachment')}
                </a>
              )}
            </MessageTextWrapper>
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.MONTHLY_ATTESTATION_TIME && (
          <>
            <MessageTextWrapper>{userName} </MessageTextWrapper>
            {notification.meta.text}
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.PLAN_START_DATE && (
          <>{notification.meta.text}</>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.SUGGESTED_GOAL_MESSAGE && (
          <>{notification.meta.text}</>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.ACCEPTED_GOAL_MESSAGE && (
          <>{notification.meta.text.split('@@@')[0]}</>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.DECLINED_GOAL_MESSAGE && (
          <>{notification.meta.text.split('@@@')[0]}</>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.INTERNAL_ANNOUNCEMENT && (
          <>
            <MessageTextWrapper>{userName} </MessageTextWrapper>
            <div dangerouslySetInnerHTML={{ __html: sanitize(notification.meta.html) }} />
          </>
        )}
        {notification.notificationType === IUSER_NOTIFICATION_TYPES.CALENDAR_MEETING_UPDATED && (
          <>
            <MessageTextWrapper>{userWithBusiness} </MessageTextWrapper>
            {t('notifications.calendar.edited')}
            <Button
              variant="text"
              label={t('notifications.actions.viewDetails')}
              className="text-app-primary-75 px-3 py-1 rounded-full text-sm hover:bg-app-primary-10 transition-colors duration-200 mt-2"
              onClick={() => {
                viewMeetingDetail(notification, handleClose)
              }}
            />
          </>
        )}
      </div>
    )
  }

  return (
    <div className="flex items-start gap-4 w-full">
      {/* Avatar component */}
      <div className="flex-shrink-0 mt-4">
        <AppAvatar
          size="md"
          type="mixed"
          shape="Rounded"
          text={userInitials}
          src={notification?.profilePicture}
        />
      </div>

      <div className="flex flex-col flex-grow w-[80%]">
        {renderNotificationContent()}
        {notification.createdAt &&
          ![
            IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_ADDED,
            IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_REPLY,
            IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_MENTION,
            IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_REPLY_MENTION,
            IUSER_NOTIFICATION_TYPES.PLAY_COMMENT_THREAD,
            IUSER_NOTIFICATION_TYPES.SUGGESTED_GOAL_MESSAGE,
            IUSER_NOTIFICATION_TYPES.ACCEPTED_GOAL_MESSAGE,
            IUSER_NOTIFICATION_TYPES.DECLINED_GOAL_MESSAGE,
            IUSER_NOTIFICATION_TYPES.MEETING_ACTION_ITEM_ADD,
            IUSER_NOTIFICATION_TYPES.SUGGESTED_PLAY_MESSAGE,
            IUSER_NOTIFICATION_TYPES.ACCEPTED_PLAY_MESSAGE,
            IUSER_NOTIFICATION_TYPES.DECLINED_PLAY_MESSAGE,
            IUSER_NOTIFICATION_TYPES.CALENDAR_NOTE_TAGGED,
            IUSER_NOTIFICATION_TYPES.PLAN_READY,
          ].includes(notification.notificationType) && (
            <div className="text-xs text-app-grey-60 mt-2">
              {timeDuration(notification.createdAt)}
            </div>
          )}
      </div>
    </div>
  )
}

interface INotificationContainerProps {
  headerText: string
  contentTitle: string
  contentValue?: string
  contentValueReact?: React.ReactNode
  contentSubValue?: string
  onAccept?: () => void
  onReject?: () => void
  onAcceptButtonLabel?: string
  onRejectButtonLabel?: string
  timestamp?: string
}

const NotificationContainer: React.FC<INotificationContainerProps> = ({
  headerText,
  contentTitle,
  contentValue,
  contentValueReact,
  contentSubValue,
  onAccept,
  onReject,
  onAcceptButtonLabel,
  onRejectButtonLabel,
  timestamp,
}) => {
  const { t } = useTranslation()
  return (
    <div className="space-y-2.5 w-full">
      <div className="flex items-center justify-between">
        <h3 className="font-inter font-medium text-sm text-app-primary-75">{headerText}</h3>
        {timestamp && <span className="text-app-grey-60 text-xs">{timestamp}</span>}
      </div>
      <div className="font-inter font-normal text-sm text-app-grey-80">{contentTitle}</div>
      {(contentValue || contentValueReact) && (
        <div className="font-inter font-medium text-sm text-app-black-100 break-words bg-app-grey-5 p-2.5 rounded-lg">
          {contentValueReact || contentValue}
        </div>
      )}
      {contentSubValue && (
        <div className="font-inter font-normal text-sm text-app-grey-70">{contentSubValue}</div>
      )}
      {onAccept && (
        <div className="mt-3">
          <AppButton
            size="sm"
            variant="primary"
            label={onAcceptButtonLabel || t('notifications.actions.checkItOut')}
            onClick={onAccept}
          />
        </div>
      )}
    </div>
  )
}

export default Message
